import { perUnitState, type CombatantId, type EncounterState } from "@lancer-vtt/domain";
import type { UseEncounter } from "../useEncounter";
import { actionsLeftToEnd, useConfirmEndTurn } from "../useConfirmEndTurn";

/**
 * The minimal turn controls folded into a **controlled-but-masked** NPC's state line: a player holds a control
 * grant (CTRL) without the visibility grant (EYES), so the inspector shows the read-only masked card
 * and no weapons or actions. `decisions/0179` accepts that consequence ("control without
 * visibility cannot fire"); this strip is its two mitigations' player half. The player can still
 * start and end the unit's turn, and one line says why the rest is missing, so it never reads as
 * a bug at the table.
 *
 * ACTIVATE is `activate_unit`, offered only while the unit is `"ready"` (its real legality, the
 * same `perUnitState` read the inspector header's turn controls make); END TURN is `end_turn`, only while it is
 * mid-turn, and confirms first while the unit has actions left (`useConfirmEndTurn`). The server stays the wall.
 */
export function GrantedTurnStrip({
  combatantId,
  state,
  actorId,
  encounter,
}: {
  combatantId: CombatantId;
  state: EncounterState;
  actorId: string;
  encounter: UseEncounter;
}) {
  const unitState = perUnitState(state)[combatantId];
  const active = unitState === "active";
  const activate = () => actorId && encounter.dispatch({ type: "activate_unit", actorId, combatantId });
  const endTurn = () => actorId && encounter.dispatch({ type: "end_turn", actorId, combatantId });
  const turn = state.activation.turn;
  const ap = active && turn.phase === "acting" ? turn.budget.actions : 0;
  const c = state.combatants[combatantId];
  const confirm = useConfirmEndTurn({ actionsLeft: active && !!c && actionsLeftToEnd({ ap, combatant: c }), ap, onEnd: endTurn });
  return (
    <div className="unit-sheet-granted" role="group" aria-label="granted turn">
      <span className="cw-microlabel cw-granted-note">You drive this unit; its sheet is not disclosed to you.</span>
      <button
        type="button"
        className={`unit-sheet-activate${active ? " is-active" : ""}${confirm.armed ? " is-confirm" : ""}`}
        disabled={!actorId || (!active && unitState !== "ready")}
        title={active ? "End this unit's turn (end_turn)" : "Start this unit's turn (activate_unit)"}
        onClick={active ? confirm.onClick : activate}
        onPointerLeave={confirm.onPointerLeave}
      >
        {active ? confirm.label : "ACTIVATE"}
      </button>
    </div>
  );
}
